import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '@/lib/supabase';
import { useEffectiveBusinessId } from './useEffectiveBusinessId';

export interface ProjectOption {
  id: string;
  name: string;
  status: string;
  customerName: string | null;
}

interface UseProjectOptionsReturn {
  projects: ProjectOption[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useProjectOptions(statusFilter?: string[]): UseProjectOptionsReturn {
  const effectiveBusinessId = useEffectiveBusinessId();
  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Memoize the status filter to prevent unnecessary re-renders
  const stableStatusFilter = useMemo(
    () => statusFilter,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(statusFilter)]
  );

  const fetchProjects = useCallback(async () => {
    if (!effectiveBusinessId) {
      setProjects([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      let query = supabase
        .from('v_projects_with_customer')
        .select('id, project_name, status, customer_name')
        .eq('business_id', effectiveBusinessId)
        .order('project_name', { ascending: true });

      if (stableStatusFilter && stableStatusFilter.length > 0) {
        query = query.in('status', stableStatusFilter);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) {
        throw fetchError;
      }

      setProjects(
        (data || []).map((row) => ({
          id: row.id,
          name: row.project_name,
          status: row.status,
          customerName: row.customer_name,
        }))
      );
    } catch (err) {
      console.error('Error fetching projects:', err);
      setError(err instanceof Error ? err.message : 'Failed to load projects');
    } finally {
      setIsLoading(false);
    }
  }, [effectiveBusinessId, stableStatusFilter]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  return {
    projects,
    isLoading,
    error,
    refetch: fetchProjects,
  };
}